import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Heading,
  VStack,
  HStack,
  Text,
  Spinner,
  Badge,
  Card,
  CardBody,
  SimpleGrid,
  Input,
  InputGroup,
  InputLeftElement,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { AddIcon, CopyIcon, SearchIcon, DeleteIcon } from '@chakra-ui/icons';
import { LIST_MATCH_FORMATS, DELETE_MATCH_FORMAT } from '../../Queries';
import { Footer, WithSubnavigation } from '../../Components';

export const FormatsList = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();
  const [searchQuery, setSearchQuery] = useState('');
  const [formatToDelete, setFormatToDelete] = useState(null);

  const { data, loading, error } = useQuery(LIST_MATCH_FORMATS, {
    variables: { is_template: true },
  });

  const [deleteFormat, { loading: deleting }] = useMutation(DELETE_MATCH_FORMAT, {
    onCompleted: () => {
      toast({
        title: 'Match format deleted successfully',
        status: 'success',
        duration: 3000,
      });
      onClose();
      setFormatToDelete(null);
    },
    onError: (error) => {
      toast({
        title: 'Error deleting format',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    },
    refetchQueries: [{ query: LIST_MATCH_FORMATS, variables: { is_template: true } }],
  });

  const formats = data?.listMatchFormats || [];

  // Filter formats based on search query
  const filteredFormats = useMemo(() => {
    if (!searchQuery.trim()) {
      return formats;
    }

    const query = searchQuery.toLowerCase();
    return formats.filter((format) => {
      const details = format.format_details;
      
      // Search in format name
      if (details.format_name?.toLowerCase().includes(query)) {
        return true;
      }
      
      // Search in category
      if (details.match_category?.toLowerCase().includes(query)) {
        return true;
      }
      
      // Search in discipline
      if (details.match_discipline?.toLowerCase().replace(/_/g, ' ').includes(query)) {
        return true;
      }
      
      // Search in stage aggregation method
      if (details.stage_aggregation_method?.toLowerCase().includes(query)) {
        return true;
      }
      
      // Search in divisions
      if (details.divisions?.some(div => div.toLowerCase().includes(query))) {
        return true;
      }
      
      // Search in categories
      if (details.categories?.some(cat => cat.toLowerCase().includes(query))) {
        return true;
      }
      
      return false;
    });
  }, [formats, searchQuery]);

  const handleCopyFormat = (format) => {
    // Navigate to create page with format data in state
    navigate('/matches/manage/formats/new', {
      state: {
        copyFrom: {
          format_name: `${format.format_details.format_name} (Copy)`,
          match_category: format.format_details.match_category,
          match_discipline: format.format_details.match_discipline,
          competition_type: format.format_details.competition_type || 'RANKED',
          bracket_config: format.format_details.bracket_config || null,
          stage_aggregation_method: format.format_details.stage_aggregation_method,
          divisions: format.format_details.divisions || [],
          categories: format.format_details.categories || [],
          stages: format.format_details.stages || [],
          is_template: format.format_details.is_template,
        }
      }
    });
  };

  const handleDeleteClick = (format) => {
    setFormatToDelete(format);
    onOpen();
  };

  const handleDeleteConfirm = () => {
    if (formatToDelete) {
      deleteFormat({ variables: { format_id: formatToDelete.format_id } });
    }
  };

  if (loading) {
    return (
      <Box bg="#F7F7F7" minH="100vh">
        <WithSubnavigation />
        <Box pt="84px" px={{ base: '42px', md: '84px' }} pb={8} textAlign="center">
          <Spinner size="lg" />
          <Text mt={4}>Loading formats...</Text>
        </Box>
        <Footer />
      </Box>
    );
  }

  if (error) {
    return (
      <Box bg="#F7F7F7" minH="100vh">
        <WithSubnavigation />
        <Box pt="84px" px={{ base: '42px', md: '84px' }} pb={8}>
          <Text color="red.500">Error loading formats: {error.message}</Text>
        </Box>
        <Footer />
      </Box>
    );
  }

  return (
    <Box bg="#F7F7F7" minH="100vh">
      <WithSubnavigation />
      <Box pt="84px" px={{ base: '42px', md: '84px' }} pb={8}>
        <HStack justify="space-between" mb={6}>
          <Heading size="lg">Match Formats</Heading>
          <HStack>
            <Button
              leftIcon={<AddIcon />}
              colorScheme="blue"
              onClick={() => navigate('/matches/manage/formats/new')}
            >
              Create Format
            </Button>
            <Button onClick={() => navigate('/matches/manage')}>
              Back to Matches
            </Button>
          </HStack>
        </HStack>

        {/* Search Bar */}
        {formats.length > 0 && (
          <Box mb={6}>
            <InputGroup size="lg">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search formats by name, category, discipline, divisions, or categories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                bg="white"
              />
            </InputGroup>
            {searchQuery && (
              <Text fontSize="sm" color="gray.600" mt={2}>
                Found {filteredFormats.length} format{filteredFormats.length !== 1 ? 's' : ''}
              </Text>
            )}
          </Box>
        )}

        {formats.length === 0 ? (
          <Box textAlign="center" py={10} bg="white" borderRadius="md" p={8}>
            <Text fontSize="lg" color="gray.500" mb={4}>
              No match formats created yet
            </Text>
            <Button
              leftIcon={<AddIcon />}
              colorScheme="blue"
              onClick={() => navigate('/matches/manage/formats/new')}
            >
              Create Your First Format
            </Button>
          </Box>
        ) : filteredFormats.length === 0 ? (
          <Box textAlign="center" py={10} bg="white" borderRadius="md" p={8}>
            <Text fontSize="lg" color="gray.500" mb={4}>
              No formats match your search
            </Text>
            <Text fontSize="sm" color="gray.600">
              Try a different search term or{' '}
              <Button variant="link" colorScheme="blue" onClick={() => setSearchQuery('')}>
                clear the search
              </Button>
            </Text>
          </Box>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {filteredFormats.map((format) => (
              <Card key={format.format_id}>
                <CardBody>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Heading size="md">{format.format_details.format_name}</Heading>
                      {format.format_details.is_template && (
                        <Badge colorScheme="green">Template</Badge>
                      )}
                    </HStack>

                    <HStack>
                      <Badge colorScheme="blue">
                        {format.format_details.match_category}
                      </Badge>
                      {format.format_details.match_discipline && (
                        <Badge colorScheme="purple">
                          {format.format_details.match_discipline.replace(/_/g, ' ')}
                        </Badge>
                      )}
                    </HStack>

                    <VStack align="stretch" spacing={1} fontSize="sm">
                      <Text>
                        <strong>Stages:</strong> {format.format_details.stages?.length || 0}
                      </Text>
                      <Text>
                        <strong>Stage Aggregation:</strong>{' '}
                        {format.format_details.stage_aggregation_method}
                      </Text>
                      {format.format_details.divisions?.length > 0 && (
                        <Text>
                          <strong>Divisions:</strong>{' '}
                          {format.format_details.divisions.join(', ')}
                        </Text>
                      )}
                      {format.format_details.categories?.length > 0 && (
                        <Text>
                          <strong>Categories:</strong>{' '}
                          {format.format_details.categories.join(', ')}
                        </Text>
                      )}
                    </VStack>

                    <HStack spacing={2}>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        flex={1}
                        onClick={() =>
                          navigate(`/matches/manage/formats/${format.format_id}`)
                        }
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        leftIcon={<CopyIcon />}
                        colorScheme="green"
                        variant="outline"
                        flex={1}
                        onClick={() => handleCopyFormat(format)}
                      >
                        Copy
                      </Button>
                      <Button
                        size="sm"
                        leftIcon={<DeleteIcon />}
                        colorScheme="red"
                        variant="outline"
                        flex={1}
                        onClick={() => handleDeleteClick(format)}
                      >
                        Delete
                      </Button>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </Box>
      <Footer />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Match Format
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete{' '}
              <strong>{formatToDelete?.format_details?.format_name}</strong>?
              <br />
              <br />
              This action cannot be undone. Any matches using this format will not be
              affected, but you won't be able to create new matches with this format.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteConfirm}
                ml={3}
                isLoading={deleting}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};
